import type { Uloga } from '../api/types'

/**
 * Čitanje access tokena. Klijent iz njega saznaje SAMO što mu treba za prikaz
 * (koje sučelje otvoriti, stoji li obveza promjene lozinke) — provjera potpisa i
 * svaka odluka o pravima ostaju na poslužitelju. Token bez potpisa ovdje nije
 * dokaz nego natuknica.
 */
export interface TokenClaims {
  sub: string | null
  uloga: Uloga | null
  /** Operater platforme NEMA `tenant_id` claim (CLAUDE.md §4) — otud `null`. */
  tenantId: string | null
  /**
   * Ime prijavljenog OSOBLJA, jedini claim koji se samo ispisuje. Gost ga u
   * tokenu nema: njegovo ime stoji na `GET /me/access` (CLAUDE.md §16, gost PWA,
   * odluka 2), pa je ovdje `null` i to nije nedostatak nego druga površina.
   */
  ime: string | null
  /** `pwd_change`: prisutnost je zabrana, odsutnost je normalno stanje. */
  moraPromijenitiLozinku: boolean
  /** Istek u milisekundama (epoch), radi preventivnog osvježavanja. */
  isticeMs: number | null
}

const PRAZNO: TokenClaims = {
  sub: null,
  uloga: null,
  tenantId: null,
  ime: null,
  moraPromijenitiLozinku: false,
  isticeMs: null,
}

export function procitajToken(token: string | null | undefined): TokenClaims {
  if (!token) return PRAZNO

  const tijelo = token.split('.')[1]
  if (!tijelo) return PRAZNO

  let raspakirano: Record<string, unknown>
  try {
    raspakirano = JSON.parse(base64UrlDekodiraj(tijelo)) as Record<string, unknown>
  } catch {
    return PRAZNO
  }

  const exp = raspakirano['exp']

  return {
    sub: tekst(raspakirano['sub']),
    uloga: (tekst(raspakirano['role']) as Uloga | null) ?? null,
    tenantId: tekst(raspakirano['tenant_id']),
    ime: tekst(raspakirano['ime_prezime']),
    // Claim je istina i kao string "true" i kao boolean — poslužitelj ga šalje
    // kao string, ali čitanje ne smije ovisiti o toj sitnici.
    moraPromijenitiLozinku:
      raspakirano['pwd_change'] === true || raspakirano['pwd_change'] === 'true',
    isticeMs: typeof exp === 'number' ? exp * 1000 : null,
  }
}

function tekst(vrijednost: unknown): string | null {
  return typeof vrijednost === 'string' && vrijednost.length > 0 ? vrijednost : null
}

function base64UrlDekodiraj(dio: string): string {
  const base64 = dio.replace(/-/g, '+').replace(/_/g, '/')
  const dopuna = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
  const bajtovi = atob(base64 + dopuna)

  // Claims mogu nositi dijakritiku (ime resorta) — atob daje bajtove, ne znakove.
  return decodeURIComponent(
    Array.from(bajtovi, (znak) => `%${znak.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
  )
}
